"use client";

/**
 * Locks every page on a brand's address — the shop, the website, sign-in and
 * the till — behind the outage screen while a Kuttos admin has the
 * maintenance switch on (lib/config/maintenance.ts), for everyone, and puts
 * the page back the moment it is switched off.
 *
 * The live check runs BESIDE the page, inside its own error boundary: if the
 * check fails (a deploy in flight, a blip), the brand's page is shown as
 * normal and never breaks because of this.
 */

import { Component, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { MaintenanceScreen } from "./MaintenanceScreen";

type Status = { on: boolean; note?: string };

class SilentBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    console.warn("[maintenance] status check failed — showing the page as normal", error);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function StatusProbe({ subdomain, onStatus }: { subdomain: string; onStatus: (s: Status | null) => void }) {
  const status = useQuery(api.platformAdmin.maintenance.status, { subdomain });
  useEffect(() => {
    onStatus(status ?? null);
  }, [status, onStatus]);
  return null;
}

export function MaintenanceGate({ subdomain, children }: { subdomain?: string | null; children: ReactNode }) {
  const [status, setStatus] = useState<Status | null>(null);
  const locked = !!subdomain && !!status?.on;

  return (
    <>
      {subdomain && (
        <SilentBoundary>
          <StatusProbe subdomain={subdomain} onStatus={setStatus} />
        </SilentBoundary>
      )}
      {locked ? <MaintenanceScreen note={status!.note} subdomain={subdomain} /> : children}
    </>
  );
}

export default MaintenanceGate;
